import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RecipientChecklist } from "./PrototypeSharesCard";
import { errorMessage, fieldError } from "@/api/errors";
import { useAdminOrg, useAdminProject } from "@/api/hooks/admin";
import {
  useAdminPrototypeBatches,
  useCreatePrototypeShare,
  usePatchPrototypeShare,
  type AdminPrototypeBatch,
  type AdminPrototypeShare,
} from "@/api/hooks/prototypes";
import type { AdminOrgUser, PrototypeSharePatch } from "@/api/types";
import { usePageCrumbs } from "@/app/layouts/shellContext";
import { ErrorNotice } from "@/components/ErrorNotice";
import { Field, FormError } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  codeLeakWarning,
  currentVersion,
  latestBatchUnconfirmed,
  nextBatchNumber,
  nextVersion,
  recipientOptions,
} from "@/features/prototypes/shares";
import { Markdown } from "@/lib/markdown";

const NAME_MAX = 120;
const URL_MAX = 500;
const CODE_MAX = 64;

type ShareValues = { batch_number: string; batch_name: string; url: string; access_code: string; message: string };
type ShareErrors = Partial<Record<keyof ShareValues, string>>;

/**
 * What the form is for: editing one version, sharing the next version of an existing batch
 * (`?batch=N`), or sharing a new batch (any unused number up to the next one, version 1).
 */
type ShareMode =
  | { kind: "edit"; share: AdminPrototypeShare }
  | { kind: "version"; batch: AdminPrototypeBatch }
  | { kind: "new"; batches: AdminPrototypeBatch[] };

function useValidate(mode: ShareMode) {
  const { t } = useTranslation();
  return (v: ShareValues): ShareErrors => {
    const errors: ShareErrors = {};
    if (mode.kind === "new") {
      const n = /^\d+$/.test(v.batch_number.trim()) ? Number(v.batch_number.trim()) : NaN;
      if (!(n >= 1 && n <= nextBatchNumber(mode.batches))) errors.batch_number = t("admin.prototype.batchNumberInvalid");
      else if (mode.batches.some((b) => b.number === n)) errors.batch_number = t("admin.prototype.batchNumberTaken", { number: n });
    }
    if (!v.batch_name.trim()) errors.batch_name = t("admin.prototype.nameRequired");
    else if (v.batch_name.trim().length > NAME_MAX) errors.batch_name = t("admin.prototype.nameTooLong");
    if (!/^https:\/\/\S+$/i.test(v.url.trim())) errors.url = t("admin.prototype.linkInvalid");
    if (!v.message.trim()) errors.message = t("admin.prototype.messageRequired");
    return errors;
  };
}

/** Live approximation of the `prototype_shared` email, so staff see what clients will read. */
function EmailPreview({
  projectName,
  label,
  values,
  recipientName,
}: {
  projectName: string;
  label: string;
  values: ShareValues;
  recipientName: string | null;
}) {
  const { t } = useTranslation();
  const batch = values.batch_name.trim() || t("admin.prototype.previewBatch");
  return (
    <section aria-labelledby="email-preview-title">
    <Card>
      <CardHeader className="gap-1">
        <h2 id="email-preview-title" className="text-xl">
          {t("admin.prototype.previewTitle")}
        </h2>
        <p className="text-sm text-fg-muted">{t("admin.prototype.previewHint")}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="border-b border-border pb-3 text-sm">
          <span className="font-medium">{t("admin.prototype.previewSubject")}</span>{" "}
          {t("admin.prototype.previewSubjectLine", { project: projectName, label })}
        </p>
        <h3 className="text-lg">{t("admin.prototype.previewHeading", { label })}</h3>
        <p>{t("admin.prototype.previewGreeting", { name: recipientName ?? t("admin.prototype.previewThere") })}</p>
        <p>{t("admin.prototype.previewIntro", { project: projectName })}</p>
        <p>{t("admin.prototype.previewBatchLine", { label, batch })}</p>
        {values.message.trim() ? <Markdown source={values.message} /> : <p className="text-fg-muted">{t("markdown.nothing")}</p>}
        {values.url.trim() ? (
          <p className="break-all">
            {t("admin.prototype.previewOpen")} <span className="text-link underline">{values.url.trim()}</span>
          </p>
        ) : null}
        {values.access_code.trim() ? <p>{t("admin.prototype.previewCode")}</p> : null}
        <div>
          <span className="inline-flex min-h-11 items-center rounded-full bg-primary px-6 font-semibold text-primary-fg">
            {t("admin.prototype.previewButton")}
          </span>
        </div>
      </CardContent>
    </Card>
    </section>
  );
}

function ShareForm({
  projectId,
  projectName,
  users,
  mode,
  onDone,
}: {
  projectId: string;
  projectName: string;
  users: AdminOrgUser[];
  mode: ShareMode;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const validate = useValidate(mode);
  const create = useCreatePrototypeShare(projectId);
  const patch = usePatchPrototypeShare(projectId);
  const options = recipientOptions(users);
  const share = mode.kind === "edit" ? mode.share : undefined;
  const [values, setValues] = useState<ShareValues>(() => {
    if (mode.kind === "edit") {
      const s = mode.share;
      return { batch_number: String(s.batch_number), batch_name: s.batch_name, url: s.url, access_code: s.access_code ?? "", message: s.message };
    }
    if (mode.kind === "version") {
      const b = mode.batch;
      const base = currentVersion(b.versions) ?? [...b.versions].sort((x, y) => y.version - x.version)[0];
      return {
        batch_number: String(b.number),
        batch_name: b.name,
        url: base?.url ?? "",
        access_code: base?.access_code ?? "",
        message: t("admin.prototype.versionTemplate"),
      };
    }
    return {
      batch_number: String(nextBatchNumber(mode.batches)),
      batch_name: "",
      url: "",
      access_code: "",
      message: t("admin.prototype.template"),
    };
  });
  const version = mode.kind === "edit" ? mode.share.version : mode.kind === "version" ? nextVersion(mode.batch) : 1;
  const label = t("prototype.label", { batch: values.batch_number.trim() || "…", version });
  const [recipients, setRecipients] = useState<string[]>(options.filter((o) => o.eligible).map((o) => o.id));
  const [errors, setErrors] = useState<ShareErrors>({});
  const mutation = share ? patch : create;
  const set = (key: keyof ShareValues) => (value: string) => setValues((v) => ({ ...v, [key]: value }));
  const serverField = (f: string) => fieldError(mutation.error, f);
  const fieldNames = ["batch_number", "version", "batch_name", "url", "access_code", "message", "recipient_user_ids"];
  const serverError = mutation.isError && !fieldNames.some(serverField) ? errorMessage(mutation.error, t("errors.generic")) : null;
  const firstRecipient = options.find((o) => recipients.includes(o.id));

  const submit = () => {
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    const clean = {
      batch_name: values.batch_name.trim(),
      url: values.url.trim(),
      access_code: values.access_code.trim() || null,
      message: values.message,
    };
    if (share) {
      const body: PrototypeSharePatch = {};
      if (clean.batch_name !== share.batch_name) body.batch_name = clean.batch_name;
      if (clean.url !== share.url) body.url = clean.url;
      if (clean.access_code !== share.access_code) body.access_code = clean.access_code;
      if (clean.message !== share.message) body.message = clean.message;
      patch.mutate(
        { id: share.id, body },
        {
          onSuccess: () => {
            toast.success(t("admin.prototype.saved"));
            onDone();
          },
        },
      );
      return;
    }
    const isVersion = mode.kind === "version";
    create.mutate(
      { batch_number: Number(values.batch_number.trim()), version, ...clean, recipient_user_ids: recipients },
      {
        onSuccess: () => {
          const count = recipients.length;
          toast.success(
            isVersion
              ? count > 0
                ? t("admin.prototype.sharedVersionEmailed", { count })
                : t("admin.prototype.sharedVersion")
              : count > 0
                ? t("admin.prototype.sharedEmailed", { count })
                : t("admin.prototype.shared"),
          );
          onDone();
        },
      },
    );
  };

  return (
    <form
      noValidate
      className="grid gap-6 lg:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex min-w-0 flex-col gap-5">
        {mode.kind === "new" && latestBatchUnconfirmed(mode.batches) ? (
          <Banner tone="warning">{t("admin.prototype.unconfirmedWarning")}</Banner>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          {mode.kind === "new" ? (
            <Field
              label={t("admin.prototype.batchNumber")}
              hint={t("admin.prototype.batchNumberHint")}
              error={errors.batch_number ?? serverField("batch_number")}
            >
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={nextBatchNumber(mode.batches)}
                  step={1}
                  value={values.batch_number}
                  onChange={(e) => set("batch_number")(e.target.value)}
                />
              )}
            </Field>
          ) : null}
          <Field label={t("admin.prototype.version")} error={serverField("version")}>
            {(p) => <Input {...p} readOnly value={String(version)} />}
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("admin.prototype.batchName")} error={errors.batch_name ?? serverField("batch_name")}>
            {(p) => (
              <Input
                {...p}
                readOnly={mode.kind === "version"}
                value={values.batch_name}
                maxLength={NAME_MAX}
                placeholder={t("admin.prototype.batchNamePlaceholder")}
                onChange={(e) => set("batch_name")(e.target.value)}
              />
            )}
          </Field>
          <Field label={t("admin.prototype.code")} hint={t("admin.prototype.codeHint")} error={serverField("access_code")}>
            {(p) => (
              <Input
                {...p}
                value={values.access_code}
                maxLength={CODE_MAX}
                autoComplete="off"
                onChange={(e) => set("access_code")(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label={t("admin.prototype.link")} hint={t("admin.prototype.linkHint")} error={errors.url ?? serverField("url")}>
          {(p) => (
            <Input
              {...p}
              type="url"
              inputMode="url"
              value={values.url}
              maxLength={URL_MAX}
              placeholder="https://"
              onChange={(e) => set("url")(e.target.value)}
            />
          )}
        </Field>
        <Field label={t("admin.prototype.message")} hint={t("admin.prototype.messageHint")} error={errors.message ?? serverField("message")}>
          {(p) => (
            <Textarea
              {...p}
              rows={22}
              maxLength={20000}
              value={values.message}
              onChange={(e) => set("message")(e.target.value)}
            />
          )}
        </Field>
        {codeLeakWarning(values.access_code, values.message, values.url) ? (
          <Banner tone="warning">{t("admin.prototype.codeLeak")}</Banner>
        ) : null}
        {share ? (
          <p className="text-sm text-fg-muted">{t("admin.prototype.editNote")}</p>
        ) : (
          <RecipientChecklist options={options} selected={recipients} onChange={setRecipients} />
        )}
        <FormError message={serverField("recipient_user_ids") ?? serverError} />
        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={mutation.isPending}>
            {share ? t("admin.prototype.save") : mutation.isPending ? t("admin.prototype.sharing") : t("admin.prototype.share")}
          </Button>
          <Button variant="ghost" onClick={onDone}>
            {t("common.cancel")}
          </Button>
        </div>
      </div>
      <div className="min-w-0 lg:sticky lg:top-16 lg:self-start">
        <EmailPreview projectName={projectName} label={label} values={values} recipientName={firstRecipient?.name ?? null} />
      </div>
    </form>
  );
}

/**
 * Admin › Project › Share a new batch, a new version of one (`?batch=N`), or edit a version:
 * full-width form with a live email preview.
 */
export function PrototypeSharePage() {
  const { t } = useTranslation();
  const { id = "", shareId } = useParams();
  const [search] = useSearchParams();
  const batchParam = shareId ? null : search.get("batch");
  const navigate = useNavigate();
  const project = useAdminProject(id);
  const org = useAdminOrg(project.data?.organisation_id);
  const batches = useAdminPrototypeBatches(id);
  const share = shareId ? batches.data?.flatMap((b) => b.versions).find((s) => s.id === shareId) : undefined;
  const batch = batchParam ? batches.data?.find((b) => String(b.number) === batchParam) : undefined;
  const title = shareId
    ? t("admin.prototype.editTitle", { name: share?.batch_name ?? "" })
    : batchParam
      ? t("admin.prototype.newVersionTitle", { number: batch?.number ?? batchParam })
      : t("admin.prototype.newTitle");
  const back = `/admin/projects/${id}`;
  usePageCrumbs([
    { label: t("admin.nav.orgs"), to: "/admin" },
    ...(org.data ? [{ label: org.data.name, to: `/admin/orgs/${org.data.id}` }] : []),
    ...(project.data ? [{ label: project.data.name, to: back }] : []),
    { label: title },
  ]);

  if (project.isPending || org.isPending || batches.isPending) return <PageLoading />;
  if (project.isError) return <ErrorNotice error={project.error} />;
  if (org.isError) return <ErrorNotice error={org.error} />;
  if (batches.isError) return <ErrorNotice error={batches.error} />;
  if ((shareId && !share) || (batchParam && !batch)) return <Banner tone="danger">{t("admin.prototype.notFound")}</Banner>;
  const mode: ShareMode = share
    ? { kind: "edit", share }
    : batch
      ? { kind: "version", batch }
      : { kind: "new", batches: batches.data };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={title}
        description={
          <Link to={back} className="inline-flex items-center gap-1">
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            {t("admin.prototype.backToProject", { name: project.data.name })}
          </Link>
        }
      />
      <ShareForm
        key={share?.id ?? `new-${batch?.number ?? ""}`}
        projectId={id}
        projectName={project.data.name}
        users={org.data.users}
        mode={mode}
        onDone={() => navigate(back)}
      />
    </div>
  );
}
